'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import clsx from 'clsx'
import useSWRInfinite from 'swr/infinite'
import { message } from 'antd'
import { CloseCircleOutlined } from '@ant-design/icons'
import { useDebounceFn } from 'ahooks'
import useI18n from 'Hooks/useI18n'
import SearchIcon from 'Comps/svg/SearchIcon'
import CommonModal from '@/components/modal/CommonModal'
import emptySymbol from '@/icons/empty_symbol.png'
import {
  addUserToBlacklist,
  getBlacklistedUserList,
  getBlacklistedUserListKey,
  removeUserFromBlacklist,
} from 'Apis/user'
import { getCreatorSearch } from 'Apis/character'
import { User } from 'Types/user'

const PAGE_SIZE = 20
const INPUT_MAX_LENGTH = 20
type UserRow = Pick<User, 'user_id' | 'nickname' | 'avatar' | 'avatar_url'>

type Props = {
  open: boolean
  onClose: () => void
}

export default function BlacklistModal({ open, onClose }: Props) {
  const { t } = useI18n()
  const [activeTab, setActiveTab] = useState<1 | 2>(1)
  const [mode, setMode] = useState<'list' | 'search'>('list')
  const [query, setQuery] = useState('')
  const [userList, setUserList] = useState<UserRow[]>([])
  const [loading, setLoading] = useState(false)

  const TAB_ITEMS = [
    { key: 1, label: t('Hide Others') },
    { key: 2, label: t('Hide Me') },
  ]

  const { data, size, setSize, isValidating, mutate } = useSWRInfinite(
    getBlacklistedUserListKey(PAGE_SIZE, activeTab),
    ({ req: { page, page_size, block_type } }) =>
      getBlacklistedUserList(page, page_size, block_type),
    {
      keepPreviousData: true,
      onError: (err) => {
        console.error('getBlacklistedUserList error:', err)
      },
    }
  )

  useEffect(() => {
    setSize(1)
  }, [activeTab, setSize])

  useEffect(() => {
    if (!open) {
      setMode('list')
      setQuery('')
      setUserList([])
    }
  }, [open])

  const users = useMemo(() => {
    return data?.flatMap((page) => page?.users ?? []) ?? []
  }, [data])

  const total = useMemo(() => data?.[0]?.total || 0, [data])
  const isReachingEnd = total === users.length

  const handleRemove = async (blocked_user_id: string) => {
    try {
      await removeUserFromBlacklist(blocked_user_id, activeTab)
      message.success(t('Success'))
      await mutate()
    } catch (error) {
      console.error('Failed to remove from blacklist', error)
    }
  }

  const handleAdd = async (user_id?: string) => {
    if (!user_id) return
    try {
      await addUserToBlacklist(user_id, activeTab)
      message.open({
        type: 'success',
        content: t('Success'),
        style: {
          marginTop: '45vh', // 约等于画面中间
        },
      })
      await mutate()
    } catch (error) {
      message.open({
        type: 'error',
        content: error?.toString() || t('Failed'),
        style: {
          marginTop: '45vh', // 约等于画面中间
        },
      })
      console.error('Failed to add to blacklist', error)
    }
  }

  const creatorQuery = useCallback(
    async (searchText: string, page: number = 1) => {
      setLoading(true)
      try {
        const data = await getCreatorSearch(searchText, page, false)
        if (page === 1) {
          setUserList(data?.hits || [])
        } else {
          setUserList((prev) => [...prev, ...(data?.hits || [])])
        }
      } catch (error) {
        const errorMessage = error?.toString() || t('Failed')
        message.error({
          content: errorMessage,
        })
      }
      setLoading(false)
    },
    [t]
  )

  const { run } = useDebounceFn(
    async (searchText: string) => {
      if (!searchText.trim()) {
        return
      }
      creatorQuery(searchText)
    },
    { wait: 150 }
  )

  const handleInputClear = () => {
    setQuery('')
    setUserList([])
  }

  const handleSearchMode = () => {
    setMode('search')
  }

  const handleBackToList = () => {
    setMode('list')
    handleInputClear()
  }

  const renderUserRow = (
    user: UserRow,
    actionLabel: string,
    onAction: () => void
  ) => {
    const avatar = user.avatar_url || user.avatar || '/profile.png'
    return (
      <div
        key={user.user_id || `${user.nickname}-${avatar}`}
        className="flex items-center gap-3 rounded-lg bg-white/5 px-3 py-3"
      >
        <Image
          src={avatar}
          alt=""
          width={48}
          height={48}
          className="h-12 w-12 rounded-full object-cover"
        />
        <div className="min-w-0 flex-1">
          <div
            className="truncate text-[14px] text-white/90"
            title={user.nickname}
          >
            {user.nickname}
          </div>
          <div className="text-[12px] text-white/40">
            {/* i18n-ignore */ 'UID:'} {user.user_id}
          </div>
        </div>
        <button
          type="button"
          className="h-8 rounded-full bg-white/10 px-4 text-[13px] text-white"
          onClick={onAction}
        >
          {actionLabel}
        </button>
      </div>
    )
  }

  return (
    <CommonModal
      open={open}
      onCancel={onClose}
      isLoading={false}
      width={560}
      bodyStyle={{ height: 800 }}
      destroyOnClose
    >
      <div className="flex w-full flex-col gap-4 px-2 pb-1 text-white">
        <div className="flex items-center justify-between pt-8">
          {mode === 'list' && (
            <div className="text-base">{t('Blocked Users')}</div>
          )}
          {mode === 'list' ? (
            <button
              type="button"
              className="flex flex-row items-center rounded-full bg-white/10 py-1 pl-2 pr-4 text-[13px] text-white"
              onClick={handleSearchMode}
            >
              <SearchIcon color="#634C54" />
              {t('Add')}
            </button>
          ) : (
            <button
              type="button"
              className="rounded-full bg-white/10 px-4 py-1 text-[13px] text-white"
              onClick={handleBackToList}
            >
              {t('Back')}
            </button>
          )}
        </div>

        <div className="flex items-center gap-6 text-[14px]">
          {TAB_ITEMS.map((tab) => {
            const isActive = tab.key === activeTab
            return (
              <button
                key={tab.key}
                type="button"
                className={`relative pb-1 ${
                  isActive ? 'text-white' : 'text-white/40'
                }`}
                onClick={() => setActiveTab(tab.key as 1 | 2)}
              >
                <span className="text-sm">{tab.label}</span>
                {isActive ? (
                  <span className="absolute bottom-0 left-1/2 h-[2px] w-[24px] -translate-x-1/2 rounded-full bg-[#9C4844]" />
                ) : null}
              </button>
            )
          })}
        </div>

        {mode === 'search' ? (
          <div className="space-y-4">
            <div className="flex h-11 items-center gap-2 rounded-full border border-[#403337] px-3">
              <SearchIcon color="#634C54" />
              <input
                value={query}
                onChange={(e) => {
                  const nextValue = e.target.value
                  setQuery(nextValue)
                  if (!nextValue.trim()) {
                    setUserList([])
                    return
                  }
                  run(nextValue.trim())
                }}
                type="text"
                maxLength={INPUT_MAX_LENGTH}
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-white/30"
                placeholder={t('Enter UID')}
              />
              <i
                className={clsx(query.length > 0 ? 'visible' : 'invisible')}
                onClick={handleInputClear}
              >
                <CloseCircleOutlined />
              </i>
            </div>

            {!loading && query.trim() && userList.length === 0 ? (
              <div className="py-6 text-center text-[14px] text-white/60">
                {t('No results')}
              </div>
            ) : null}

            <div className="space-y-3">
              {userList.map((user) =>
                renderUserRow(user, t('Add'), () => handleAdd(user.user_id))
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10">
                <Image src={emptySymbol} alt="" width={159} height={32} />
                <div className="-mt-4 w-48 text-center text-[13px] text-white/30">
                  {activeTab === 1
                    ? t('Add users you don’t want to see')
                    : t("Add users you don't want them to see you")}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((user) =>
                  renderUserRow(user, t('Remove'), () =>
                    handleRemove(user.user_id)
                  )
                )}
              </div>
            )}

            {!isReachingEnd ? (
              <button
                type="button"
                className="w-full rounded-full bg-white/10 py-2 text-[13px] text-white/80"
                onClick={() => setSize(size + 1)}
                disabled={isValidating}
              >
                {isValidating ? t('Loading') : t('Load More')}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </CommonModal>
  )
}
